import {
  AuthCredential,
  AuthError,
  GithubAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithPopup,
  signOut,
  User,
} from "firebase/auth";
import Cookies from "js-cookie";
import { create } from "zustand";
import { UserApi } from "../api/userApi";
import { auth, githubProvider, googleProvider } from "../config/firebase_config";

// Pending credential info for account linking
export interface PendingCredentialInfo {
  credential: AuthCredential;
  email: string;
  pendingProvider: string; // The provider user tried to sign in with (e.g., 'github.com')
}

interface AuthActions {
  initialize: () => void;
  signInWithGoogle: () => Promise<boolean>; // Returns true if sign-in succeeded, false if linking is needed
  signInWithGithub: () => Promise<boolean>; // Returns true if sign-in succeeded, false if linking is needed
  signInWithGoogleAndLink: () => Promise<void>;
  signInWithGithubAndLink: () => Promise<void>;
  clearPendingCredential: () => void;
  hasAccessToProject: (projectId: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  dispose: () => void;
}

interface AuthState {
  user: User | null;
  isInitialized: boolean;
  hasUserManuallySignedOut: boolean;
  pendingCredentialInfo: PendingCredentialInfo | null;

  actions: AuthActions;
}

export const useAuthStore = create<AuthState>((set, get) => {
  let unsubscribeAuthStateChanged: (() => void) | null = null;
  let unsubscribeIdTokenChanged: (() => void) | null = null;
  let isSignInInProgress = false;

  // Helper to handle successful sign-in
  const handleSuccessfulSignIn = async (user: User) => {
    const initUserKey = `darvin-user-initialized-${user.uid}`;
    const hasCalledInitUser = localStorage.getItem(initUserKey);
    if (hasCalledInitUser !== 'true') {
      await UserApi.initUser();
      localStorage.setItem(initUserKey, 'true');
    }

    set({
      user: user,
      isInitialized: true,
      hasUserManuallySignedOut: false,
      pendingCredentialInfo: null,
    });
  };

  // Helper to handle account-exists-with-different-credential error
  const handleAccountExistsError = (error: AuthError, pendingProvider: string) => {
    if (error.code === 'auth/account-exists-with-different-credential') {
      // Get the credential from the error
      const credential = pendingProvider === 'github.com' 
        ? GithubAuthProvider.credentialFromError(error)
        : GoogleAuthProvider.credentialFromError(error);
      
      // Get the email from customData
      const email = (error.customData?.email as string) || '';
      
      if (credential && email) {
        set({
          pendingCredentialInfo: {
            credential,
            email,
            pendingProvider,
          },
        });
        
        // Don't re-throw, let the UI handle the linking flow
        return true;
      }
    }
    return false;
  };

  return {
    user: null,
    isInitialized: false,
    hasUserManuallySignedOut: false,
    pendingCredentialInfo: null,

    actions: {
      initialize: () => {
        unsubscribeAuthStateChanged = onAuthStateChanged(auth, (user) => {
          // it fires before initUser finishes and it can break stuff
          if (isSignInInProgress) {
            return;
          }
          set({ user: user, isInitialized: true });
        });

        unsubscribeIdTokenChanged = onIdTokenChanged(auth, async (user) => {
          if (user) {
            const authTokenData = await user.getIdTokenResult();
            Cookies.set("session", authTokenData.token, {
              expires: new Date(authTokenData.expirationTime),
              secure: true,
              sameSite: "strict",
            });
          }
        });
      },

      signInWithGoogle: async () => {
        isSignInInProgress = true;
        try {
          const result = await signInWithPopup(auth, googleProvider);
          const user = result.user;

          if (!user) {
            throw new Error("No user found after sign in.");
          }

          await handleSuccessfulSignIn(user);
          return true; // Sign-in successful
        } catch (error) {
          const handled = handleAccountExistsError(error as AuthError, 'google.com');
          if (!handled) {
            throw error;
          }
          return false; // Linking needed
        } finally {
          isSignInInProgress = false;
        }
      },

      signInWithGithub: async () => {
        isSignInInProgress = true;
        try {
          const result = await signInWithPopup(auth, githubProvider);
          const user = result.user;

          if (!user) {
            throw new Error("No user found after sign in.");
          }

          await handleSuccessfulSignIn(user);
          return true; // Sign-in successful
        } catch (error) {
          const handled = handleAccountExistsError(error as AuthError, 'github.com');
          if (!handled) {
            throw error;
          }
          return false; // Linking needed
        } finally {
          isSignInInProgress = false;
        }
      },

      // Sign in with Google and link pending credential
      signInWithGoogleAndLink: async () => {
        const pendingCredentialInfo = get().pendingCredentialInfo;
        if (!pendingCredentialInfo) {
          throw new Error("No pending credential to link");
        }

        isSignInInProgress = true;
        try {
          const result = await signInWithPopup(auth, googleProvider);
          const user = result.user;

          if (!user) {
            throw new Error("No user found after sign in.");
          }

          // Link the pending credential to this account
          await linkWithCredential(user, pendingCredentialInfo.credential);

          await handleSuccessfulSignIn(user);
        } finally {
          isSignInInProgress = false;
        }
      },

      // Sign in with GitHub and link pending credential
      signInWithGithubAndLink: async () => {
        const pendingCredentialInfo = get().pendingCredentialInfo;
        if (!pendingCredentialInfo) {
          throw new Error("No pending credential to link");
        }

        isSignInInProgress = true;
        try {
          const result = await signInWithPopup(auth, githubProvider);
          const user = result.user;

          if (!user) {
            throw new Error("No user found after sign in.");
          }

          // Link the pending credential to this account
          await linkWithCredential(user, pendingCredentialInfo.credential);

          await handleSuccessfulSignIn(user);
        } finally {
          isSignInInProgress = false;
        }
      },

      clearPendingCredential: () => {
        set({ pendingCredentialInfo: null });
      },

      hasAccessToProject: async (projectId: string) => {
        return await UserApi.hasAccessToProject(projectId);
      },

      signOut: async () => {
        set({
          hasUserManuallySignedOut: true,
        });
        localStorage.removeItem("projects-history");
        localStorage.removeItem("model-selection");
        try {
          await signOut(auth);
          set({
            user: null,
          });
        } catch (error) {
          console.error("Error signing out:", error);
          throw error;
        }
      },

      dispose: () => {
        if (unsubscribeAuthStateChanged) {
          unsubscribeAuthStateChanged();
          unsubscribeAuthStateChanged = null;
        }
        if (unsubscribeIdTokenChanged) {
          unsubscribeIdTokenChanged();
          unsubscribeIdTokenChanged = null;
        }
      },
    },
  };
});

export const useIsAuthInitialized = () =>
  useAuthStore((state) => state.isInitialized);

export const useIsUserSignedIn = () =>
  useAuthStore((state) => state.user !== null);

export const useAuthUser = () => useAuthStore((state) => state.user);

export const useHasUserManuallySignedOut = () =>
  useAuthStore((state) => state.hasUserManuallySignedOut);

export const usePendingCredentialInfo = () =>
  useAuthStore((state) => state.pendingCredentialInfo);

export const useAuthActions = () => useAuthStore((state) => state.actions);
